/* ==========================================================================
   Stride — theme.js (vanilla JS, no dependencies)
   Modules: helpers, drawers, announcement, header, sliders, gallery, product form,
   cart (ajax), quick view, predictive search, wishlist, recently viewed, collection
   filters, countdown, tabs.
   Behaviour that needs per-element setup lives in initAll(root) so it re-runs when a
   section is re-rendered in the theme editor (shopify:section:load).
   ========================================================================== */
(function () {
  'use strict';

  var S = window.Stride || { routes: {}, settings: {}, strings: {}, currency: 'USD', locale: 'en' };

  /* ---------- Helpers ---------- */
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var esc = function (s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  var debounce = function (fn, wait) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); }; };
  var parseHTML = function (html) { return new DOMParser().parseFromString(html, 'text/html'); };
  var store = {
    get: function (k, d) { try { var v = localStorage.getItem('stride:' + k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
    set: function (k, v) { try { localStorage.setItem('stride:' + k, JSON.stringify(v)); } catch (e) { /* storage unavailable */ } }
  };
  var money = function (cents) {
    try { return new Intl.NumberFormat(S.locale, { style: 'currency', currency: S.currency, maximumFractionDigits: 0 }).format(cents / 100); }
    catch (e) { return (cents / 100).toFixed(0) + ' ' + S.currency; }
  };
  var jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* ---------- Toast ---------- */
  var Toast = {
    el: null, timer: null,
    show: function (msg) {
      this.el = this.el || $('[data-toast]');
      if (!this.el) return;
      this.el.textContent = msg; this.el.hidden = false;
      var el = this.el;
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
      clearTimeout(this.timer);
      this.timer = setTimeout(function () { el.classList.remove('is-visible'); setTimeout(function () { el.hidden = true; }, 300); }, 2600);
    }
  };

  /* ---------- Drawers (cart, mobile menu, search, filters) ---------- */
  var Drawer = {
    current: null, lastFocus: null,
    overlay: function () { return $('[data-overlay]'); },
    open: function (el) {
      if (!el) return;
      if (this.current && this.current !== el) this.close(true);
      var ov = this.overlay();
      this.lastFocus = document.activeElement;
      el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false');
      if (ov) { ov.hidden = false; requestAnimationFrame(function () { ov.classList.add('is-visible'); }); }
      document.body.classList.add('is-locked');
      this.current = el;
      var focusTarget = $('[data-autofocus]', el) || $('button, a[href], input', el);
      setTimeout(function () { if (focusTarget) focusTarget.focus({ preventScroll: true }); }, 60);
    },
    close: function (keepOverlay) {
      var el = this.current; if (!el) return;
      el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true');
      this.current = null;
      if (!keepOverlay) {
        var ov = this.overlay();
        if (ov) { ov.classList.remove('is-visible'); setTimeout(function () { if (!Drawer.current) ov.hidden = true; }, 280); }
        document.body.classList.remove('is-locked');
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      }
    },
    trap: function (e) {
      if (e.key !== 'Tab' || !this.current) return;
      var f = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea', this.current).filter(function (n) { return n.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* ---------- Announcement bar ---------- */
  function initAnnouncement(root) {
    $$('[data-announcement]', root).forEach(function (bar) {
      if (bar.__init) return; bar.__init = true;
      var slides = $$('.announcement__slide', bar), i = 0, timer;
      if (slides.length < 2) return;
      var show = function (n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); s.setAttribute('aria-hidden', k !== i); });
      };
      var play = function () {
        if (bar.dataset.autoplay !== 'true') return;
        clearInterval(timer);
        timer = setInterval(function () { show(i + 1); }, (parseInt(bar.dataset.speed, 10) || 5) * 1000);
      };
      var prev = $('[data-ann-prev]', bar), next = $('[data-ann-next]', bar);
      if (prev) prev.addEventListener('click', function () { show(i - 1); play(); });
      if (next) next.addEventListener('click', function () { show(i + 1); play(); });
      bar.addEventListener('mouseenter', function () { clearInterval(timer); });
      bar.addEventListener('mouseleave', play);
      play();
    });
  }

  /* ---------- Header scroll state ---------- */
  var headerBound = false;
  function initHeader() {
    var root = document.documentElement;
    var measure = function () {
      var h = $('.header'); if (!h) return;
      var mobile = window.matchMedia('(max-width: 749px)').matches;
      root.style.setProperty('--header-sticky-h', mobile ? h.offsetHeight + 'px' : '0px');
    };
    var onScroll = function () {
      var h = $('.header'); if (!h) return;
      h.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    if (!headerBound) {
      headerBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', measure);
      window.addEventListener('load', measure);
    }
    var hd = $('.header');
    if (window.ResizeObserver && hd && !hd.__ro) { hd.__ro = true; new ResizeObserver(measure).observe(hd); }
    onScroll(); measure();
  }

  /* ---------- Sliders (scroll-snap + arrow buttons) ---------- */
  function initSliders(root) {
    $$('[data-slider]', root).forEach(function (sl) {
      if (sl.__init) return; sl.__init = true;
      var track = $('.slider__track', sl);
      var prev = $('[data-slider-prev]', sl.parentNode) || $('[data-slider-prev]', sl);
      var next = $('[data-slider-next]', sl.parentNode) || $('[data-slider-next]', sl);
      if (!track) return;
      var update = function () {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      var step = function (dir) { track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' }); };
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', debounce(update, 40), { passive: true });
      window.addEventListener('resize', debounce(update, 120));
      update();
    });
  }

  /* ---------- Countdown ---------- */
  function initCountdown(root) {
    $$('[data-countdown]', root).forEach(function (el) {
      if (el.__init) return; el.__init = true;
      var end = new Date(el.dataset.end).getTime();
      if (isNaN(end)) return;
      var d = $('[data-cd-days]', el), h = $('[data-cd-hours]', el), m = $('[data-cd-mins]', el), s = $('[data-cd-secs]', el);
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var tick = function () {
        var diff = end - Date.now();
        if (diff <= 0) {
          clearInterval(t);
          var timer = $('.countdown__timer', el);
          if (el.dataset.hideOnEnd === 'true') { var wrap = el.closest('.sec') || el; wrap.hidden = true; }
          else if (timer) timer.innerHTML = '<strong>' + esc(S.strings.countdownEnded) + '</strong>';
          return;
        }
        var sec = Math.floor(diff / 1000);
        if (d) d.textContent = pad(Math.floor(sec / 86400));
        if (h) h.textContent = pad(Math.floor((sec % 86400) / 3600));
        if (m) m.textContent = pad(Math.floor((sec % 3600) / 60));
        if (s) s.textContent = pad(sec % 60);
      };
      var t = setInterval(tick, 1000); tick();
    });
  }

  /* ---------- Tabs (account dashboard) ---------- */
  function initTabs(root) {
    $$('[data-tabs]', root).forEach(function (wrap) {
      if (wrap.__init) return; wrap.__init = true;
      var btns = $$('[role="tab"]', wrap);
      var scope = wrap.closest('[data-tabs-scope]') || document;
      var activate = function (id) {
        btns.forEach(function (b) { b.setAttribute('aria-selected', String(b.dataset.tab === id)); });
        $$('[data-panel]', scope).forEach(function (p) { p.hidden = p.dataset.panel !== id; });
        if (id === 'wishlist') renderWishlistList();
        if (id === 'viewed') renderViewedList();
      };
      btns.forEach(function (b) { b.addEventListener('click', function () { activate(b.dataset.tab); history.replaceState(null, '', '#' + b.dataset.tab); }); });
      var initial = location.hash.replace('#', '');
      activate(btns.some(function (b) { return b.dataset.tab === initial; }) ? initial : (btns[0] && btns[0].dataset.tab));
    });
  }

  /* ---------- Cart (ajax) ---------- */
  var Cart = {
    sections: function () {
      var ids = ['cart-drawer'];
      var page = $('[data-cart-page-section]');
      if (page) ids.push(page.dataset.cartPageSection);
      return ids.join(',');
    },
    request: function (url, body) {
      body.sections = this.sections();
      body.sections_url = window.location.pathname;
      return fetch(url, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Cart error');
          return data;
        });
      });
    },
    add: function (items) {
      var self = this;
      return this.request(S.routes.cartAdd, { items: items }).then(function (data) { self.render(data.sections); return data; });
    },
    change: function (line, quantity) {
      var self = this;
      return this.request(S.routes.cartChange, { line: line, quantity: quantity }).then(function (data) { self.render(data.sections); return data; });
    },
    render: function (sections) {
      if (!sections) return;
      var count = null;
      Object.keys(sections).forEach(function (key) {
        var doc = parseHTML(sections[key]);
        $$('[data-section-replace]', doc).forEach(function (node) {
          var target = $('[data-section-replace="' + node.dataset.sectionReplace + '"]');
          if (target) target.innerHTML = node.innerHTML;
          if (node.dataset.itemCount !== undefined) count = node.dataset.itemCount;
        });
      });
      if (count !== null) {
        $$('[data-cart-count]').forEach(function (el) { el.textContent = count; el.setAttribute('data-count', count); });
      }
    },
    afterAdd: function () {
      if (S.settings.cartType === 'drawer') { Drawer.open($('#CartDrawer')); }
      else { Toast.show(S.strings.added); }
    }
  };

  function quickAdd(btn) {
    var id = parseInt(btn.dataset.quickAdd, 10);
    btn.classList.add('is-loading');
    Cart.add([{ id: id, quantity: 1 }]).then(function () {
      btn.classList.remove('is-loading'); Cart.afterAdd();
    }).catch(function (err) { btn.classList.remove('is-loading'); Toast.show(err.message); });
  }

  /* ---------- Gallery (swipe, thumbs, zoom) ---------- */
  function initGallery(root) {
    $$('[data-gallery]', root).forEach(function (g) {
      if (g.__init) return; g.__init = true;
      var track = $('.gallery__track', g), slides = $$('.gallery__slide', g), thumbs = $$('.gallery__thumb', g), dots = $$('.gallery__dot', g);
      if (!track || !slides.length) return;
      var index = 0;
      var setActive = function (i) {
        index = i;
        thumbs.forEach(function (t, k) { t.classList.toggle('is-active', k === i); t.setAttribute('aria-current', String(k === i)); });
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
        slides.forEach(function (s) { s.classList.remove('is-zoomed'); });
      };
      var go = function (i) { track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' }); setActive(i); };
      track.addEventListener('scroll', debounce(function () {
        var i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index) setActive(i);
      }, 60), { passive: true });
      thumbs.forEach(function (t, k) { t.addEventListener('click', function () { go(k); }); });
      var toggleZoom = function (slide) { if ($('img', slide)) slide.classList.toggle('is-zoomed'); };
      slides.forEach(function (s) {
        s.addEventListener('click', function () { toggleZoom(s); });
        s.addEventListener('pointermove', function (e) {
          if (!s.classList.contains('is-zoomed')) return;
          var r = s.getBoundingClientRect();
          s.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100) + '%');
          s.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100) + '%');
        });
      });
      var zoomBtn = $('[data-gallery-zoom]', g);
      if (zoomBtn) zoomBtn.addEventListener('click', function (e) { e.stopPropagation(); toggleZoom(slides[index]); });
      g.__goToMedia = function (mediaId) {
        var k = slides.findIndex(function (s) { return s.dataset.mediaId === String(mediaId); });
        if (k > -1) go(k);
      };
      setActive(0);
    });
  }

  /* ---------- Product form (variants, quantity, sticky ATC) ---------- */
  function initProductForms(root) {
    $$('[data-product-form-root]', root).forEach(function (el) {
      if (el.__init) return; el.__init = true;
      var form = $('[data-product-form]', el);
      var jsonEl = $('[data-variants-json]', el);
      if (!form || !jsonEl) return;
      var variants = [];
      try { variants = JSON.parse(jsonEl.textContent); } catch (e) { /* ignore */ }
      var idInput = $('[data-variant-id]', el);
      var fieldsets = $$('.options', el);
      var sticky = $('[data-sticky-atc]');
      var baseUrl = el.dataset.productUrl || (window.location.pathname + window.location.search.replace(/[?&]variant=\d+/, ''));
      var updateUrl = el.dataset.updateUrl === 'true';
      var controller = null;

      var selected = function () {
        return fieldsets.map(function (fs) { var c = $('input:checked', fs); return c ? c.value : null; });
      };
      var setButtons = function (variant) {
        var buttons = $$('[data-add-to-cart], [data-buy-now], [data-sticky-submit]', el).concat(sticky ? $$('[data-sticky-submit]', sticky) : []);
        buttons.forEach(function (b) {
          var label = $('[data-add-label]', b);
          var ok = variant && variant.available;
          b.disabled = !ok;
          if (label && b.hasAttribute('data-add-to-cart')) label.textContent = !variant ? S.strings.unavailable : (ok ? S.strings.addToCart : S.strings.soldOut);
          if (b.hasAttribute('data-sticky-submit')) b.textContent = !variant ? S.strings.unavailable : (ok ? S.strings.addToCart : S.strings.soldOut);
        });
      };
      var updateAvailability = function (sel) {
        fieldsets.forEach(function (fs, i) {
          $$('input', fs).forEach(function (inp) {
            var test = sel.slice(0, i).concat(inp.value);
            var ok = variants.some(function (v) { return v.available && test.every(function (o, k) { return v.options[k] === o; }); });
            var label = fs.querySelector('label[for="' + inp.id + '"]');
            if (label) label.classList.toggle('is-unavailable', !ok);
          });
        });
      };
      var refresh = function (variant) {
        if (controller) controller.abort();
        controller = new AbortController();
        var sep = baseUrl.indexOf('?') > -1 ? '&' : '?';
        fetch(baseUrl + sep + 'variant=' + variant.id, { signal: controller.signal }).then(function (r) { return r.text(); }).then(function (html) {
          var doc = parseHTML(html);
          ['[data-price]', '[data-stock]', '[data-sku]'].forEach(function (sel) {
            var fresh = $(sel, doc), cur = $(sel, el);
            if (fresh && cur) cur.replaceWith(fresh.cloneNode(true));
          });
          if (sticky) {
            var freshPrice = $('[data-price-current]', doc), cp = $('[data-price-current]', sticky);
            if (freshPrice && cp) cp.innerHTML = freshPrice.innerHTML;
          }
        }).catch(function () { /* aborted */ });
      };
      var onChange = function () {
        var sel = selected();
        fieldsets.forEach(function (fs, i) { var out = $('[data-option-selected]', fs); if (out) out.textContent = sel[i] || ''; });
        updateAvailability(sel);
        var variant = variants.find(function (v) { return sel.every(function (o, i) { return v.options[i] === o; }); });
        setButtons(variant);
        if (!variant) return;
        idInput.value = variant.id;
        if (updateUrl) history.replaceState(null, '', window.location.pathname + '?variant=' + variant.id);
        if (variant.featured_media) {
          var g = $('[data-gallery]'); if (g && g.__goToMedia) g.__goToMedia(variant.featured_media.id);
        }
        refresh(variant);
      };
      fieldsets.forEach(function (fs) { fs.addEventListener('change', onChange); });
      updateAvailability(selected());

      /* quantity stepper */
      var qty = $('[data-qty]', el);
      if (qty) {
        var input = $('input', qty);
        qty.addEventListener('click', function (e) {
          var b = e.target.closest('button'); if (!b) return;
          var v = parseInt(input.value, 10) || 1;
          input.value = Math.max(1, v + (b.hasAttribute('data-qty-plus') ? 1 : -1));
        });
      }

      /* sticky add-to-cart (mobile) */
      if (sticky) {
        var anchor = $('[data-buy-anchor]', el);
        if (anchor && 'IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            sticky.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
          }).observe(anchor);
        }
        var sBtn = $('[data-sticky-submit]', sticky);
        if (sBtn) sBtn.addEventListener('click', function () { form.requestSubmit(); });
      }
    });
  }

  function submitProductForm(form) {
    var btn = $('[data-add-to-cart]', form) || $('[data-add-to-cart]', form.closest('[data-product-form-root]') || document);
    var fd = new FormData(form);
    var item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 };
    var props = {};
    fd.forEach(function (v, k) { var m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; });
    if (Object.keys(props).length) item.properties = props;
    if (btn) btn.classList.add('is-loading');
    return Cart.add([item]).then(function () {
      if (btn) { btn.classList.remove('is-loading'); btn.classList.add('is-done'); setTimeout(function () { btn.classList.remove('is-done'); }, 1400); }
      var qv = $('[data-qv-dialog]'); if (qv && qv.open) qv.close();
      Cart.afterAdd();
    }).catch(function (err) { if (btn) btn.classList.remove('is-loading'); Toast.show(err.message); throw err; });
  }

  function buyNow(btn) {
    var form = btn.closest('[data-product-form]') || $('[data-product-form]', btn.closest('[data-product-form-root]') || document);
    if (!form) return;
    var fd = new FormData(form);
    btn.classList.add('is-loading');
    fetch(S.routes.cartAdd, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ items: [{ id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 }] }) })
      .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || d.message); return d; }); })
      .then(function () { window.location.href = S.routes.checkout; })
      .catch(function (err) { btn.classList.remove('is-loading'); Toast.show(err.message); });
  }

  /* ---------- Quick view dialog ---------- */
  function openQuickView(url) {
    var dlg = $('[data-qv-dialog]'), body = $('[data-quick-view-body]');
    if (!dlg || !body) { window.location.href = url; return; }
    body.innerHTML = '<div class="qv"><p>' + esc(S.strings.loading) + '…</p></div>';
    dlg.showModal();
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    fetch(url + sep + 'view=quick').then(function (r) { return r.text(); }).then(function (html) {
      body.innerHTML = html;
      var root = $('[data-product-form-root]', body);
      if (root) root.dataset.productUrl = url + sep + 'view=quick';
      initProductForms(body);
    }).catch(function () { dlg.close(); window.location.href = url; });
  }

  /* ---------- Wishlist (localStorage) ---------- */
  var Wishlist = {
    get: function () { return store.get('wishlist', []); },
    has: function (h) { return this.get().indexOf(h) > -1; },
    toggle: function (h) {
      var list = this.get(), i = list.indexOf(h);
      if (i > -1) list.splice(i, 1); else list.unshift(h);
      store.set('wishlist', list); this.sync();
      return i === -1;
    },
    sync: function () {
      var list = this.get();
      $$('[data-wishlist-toggle]').forEach(function (b) {
        var on = list.indexOf(b.dataset.wishlistToggle) > -1;
        b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', on ? S.strings.wishlistRemove : S.strings.wishlistAdd);
      });
      $$('[data-wishlist-count]').forEach(function (c) { c.textContent = list.length; c.setAttribute('data-count', list.length); });
    }
  };

  function renderCards(container, handles, emptyMsg) {
    if (!container) return;
    if (!handles.length) { container.innerHTML = '<p class="muted">' + esc(emptyMsg) + '</p>'; return; }
    container.innerHTML = '';
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '?view=card').then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
    })).then(function (cards) {
      container.innerHTML = cards.filter(Boolean).join('');
      Wishlist.sync();
    });
  }
  function renderWishlistList() { $$('[data-wishlist-list]').forEach(function (c) { renderCards(c, Wishlist.get(), S.strings.wishlistEmpty); }); }
  function renderViewedList() { $$('[data-viewed-list]').forEach(function (c) { renderCards(c, store.get('viewed', []).slice(0, 8), S.strings.viewedEmpty); }); }

  /* ---------- Recently viewed ---------- */
  function trackViewed() {
    var t = $('[data-track-product]'); if (!t) return;
    var h = t.dataset.trackProduct, list = store.get('viewed', []).filter(function (x) { return x !== h; });
    list.unshift(h); store.set('viewed', list.slice(0, 16));
  }
  function initRecentlyViewed(root) {
    $$('[data-recently-viewed]', root).forEach(function (el) {
      if (el.__init) return; el.__init = true;
      var current = el.dataset.exclude, limit = parseInt(el.dataset.limit, 10) || 8;
      var handles = store.get('viewed', []).filter(function (h) { return h !== current; }).slice(0, limit);
      var list = $('[data-recently-viewed-list]', el);
      if (!handles.length || !list) return;
      Promise.all(handles.map(function (h) {
        return fetch('/products/' + encodeURIComponent(h) + '?view=card').then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
      })).then(function (cards) {
        cards = cards.filter(Boolean);
        if (!cards.length) return;
        list.innerHTML = cards.map(function (c) { return '<div>' + c + '</div>'; }).join('');
        el.hidden = false; Wishlist.sync(); initSliders(el);
      });
    });
  }

  /* ---------- Recommended products (Section Rendering via recommendations endpoint) ---------- */
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach(function (el) {
      if (el.__init) return; el.__init = true;
      fetch(el.dataset.url).then(function (r) { return r.text(); }).then(function (html) {
        var doc = parseHTML(html), fresh = $('[data-recommendations]', doc);
        if (fresh && $('.pcard', fresh)) { el.innerHTML = fresh.innerHTML; el.hidden = false; initSliders(el); Wishlist.sync(); }
      }).catch(function () { /* ignore */ });
    });
  }

  /* ---------- Frequently bought together ---------- */
  function initBundle(root) {
    $$('[data-bundle]', root).forEach(function (el) {
      if (el.__init) return; el.__init = true;
      var totalEl = $('[data-bundle-total]', el), addBtn = $('[data-bundle-add]', el);
      var rows = $$('[data-bundle-item]', el);
      var mainVariant = function () { var i = $('[data-main-variant-id]'); return i ? i.value : null; };
      var calc = function () {
        var total = 0, n = 0;
        rows.forEach(function (row) {
          var chk = $('input[type="checkbox"]', row);
          var sel = $('select', row);
          var price = sel ? parseInt(sel.options[sel.selectedIndex].dataset.price, 10) : parseInt(row.dataset.price, 10);
          if (chk.checked) { total += price; n++; }
        });
        if (totalEl) totalEl.textContent = money(total);
        if (addBtn) addBtn.disabled = n === 0;
      };
      el.addEventListener('change', calc);
      calc();
      if (addBtn) addBtn.addEventListener('click', function () {
        var items = [];
        rows.forEach(function (row) {
          var chk = $('input[type="checkbox"]', row);
          if (!chk.checked) return;
          var sel = $('select', row);
          var id = row.dataset.main === 'true' ? (mainVariant() || row.dataset.variant) : (sel ? sel.value : row.dataset.variant);
          items.push({ id: parseInt(id, 10), quantity: 1 });
        });
        addBtn.classList.add('is-loading');
        Cart.add(items).then(function () { addBtn.classList.remove('is-loading'); Cart.afterAdd(); })
          .catch(function (err) { addBtn.classList.remove('is-loading'); Toast.show(err.message); });
      });
    });
  }

  /* ---------- Predictive search ---------- */
  var Search = {
    controller: null,
    recent: function () { return store.get('recent-searches', []); },
    save: function (q) {
      q = (q || '').trim(); if (!q) return;
      var list = this.recent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
      list.unshift(q); store.set('recent-searches', list.slice(0, 6));
    },
    renderRecent: function () {
      var wrap = $('[data-search-recent]'), list = $('[data-search-recent-list]');
      if (!wrap || !list) return;
      var items = S.settings.recentSearches ? this.recent() : [];
      wrap.hidden = !items.length;
      list.innerHTML = items.map(function (q) { return '<a class="chip" href="' + S.routes.search + '?q=' + encodeURIComponent(q) + '&type=product,article,page">' + esc(q) + '</a>'; }).join('');
    },
    query: function (q) {
      var out = $('[data-psearch]'), recent = $('[data-search-recent]');
      if (!out) return;
      if (q.length < 2) { out.innerHTML = ''; this.renderRecent(); return; }
      if (recent) recent.hidden = true;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      var url = S.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last';
      fetch(url, { signal: this.controller.signal }).then(function (r) { return r.text(); }).then(function (html) {
        var fresh = $('[data-psearch-results]', parseHTML(html));
        out.innerHTML = fresh ? fresh.innerHTML : '';
      }).catch(function () { /* aborted */ });
    }
  };

  /* ---------- Collection filtering (Section Rendering API) ---------- */
  function replaceRegions(doc) {
    var openGroups = $$('details.fgroup[open]').map(function (d) { return d.dataset.group; });
    $$('[data-section-replace]', doc).forEach(function (node) {
      var target = $('[data-section-replace="' + node.dataset.sectionReplace + '"]');
      if (target) target.innerHTML = node.innerHTML;
    });
    $$('details.fgroup').forEach(function (d) { if (openGroups.indexOf(d.dataset.group) > -1) d.open = true; });
  }
  function updateCollection(urlStr, push) {
    var root = $('[data-collection]'); if (!root) return;
    var u = new URL(urlStr, window.location.origin);
    var render = new URL(u.href); render.searchParams.set('section_id', root.dataset.sectionId);
    var results = $('[data-section-replace="coll-results"]');
    if (results) results.classList.add('coll-loading');
    fetch(render.toString()).then(function (r) { return r.text(); }).then(function (html) {
      replaceRegions(parseHTML(html));
      Wishlist.sync();
      if (push !== false) history.pushState({ stride: true }, '', u.pathname + u.search);
      var sort = $('[data-sort-select]'); if (sort) sort.value = u.searchParams.get('sort_by') || sort.dataset.default || sort.value;
      if (results) results.classList.remove('coll-loading');
      var rr = $('[data-section-replace="coll-results"]'); if (rr) rr.classList.remove('coll-loading');
    }).catch(function () { window.location.href = u.toString(); });
  }
  function filterFormUrl(form) {
    var fd = new FormData(form), p = new URLSearchParams();
    fd.forEach(function (v, k) { if (v !== '' && k !== 'page') p.append(k, v); });
    var sort = $('[data-sort-select]');
    if (sort && sort.value && !p.has('sort_by')) p.set('sort_by', sort.value);
    var qs = p.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }
  var onFilterChange = debounce(function () {
    var form = $('[data-filters-form]'); if (form) updateCollection(filterFormUrl(form));
  }, 350);

  /* ---------- Slideshow (scroll-snap + arrows + dots + autoplay) ---------- */
  function initSlideshow(root) {
    $$('[data-slideshow]', root).forEach(function (ss) {
      if (ss.__init) return; ss.__init = true;
      var track = $('.slideshow__track', ss), slides = $$('.slideshow__slide', ss), dots = $$('[data-ss-dot]', ss);
      var prev = $('[data-ss-prev]', ss), next = $('[data-ss-next]', ss), i = 0, timer = null;
      if (!track || slides.length < 2) return;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var mark = function () {
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); if (k === i) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      };
      var go = function (n, instant) {
        i = (n + slides.length) % slides.length;
        track.scrollTo({ left: i * track.clientWidth, behavior: instant || reduce ? 'auto' : 'smooth' });
        mark();
      };
      var stop = function () { if (timer) { clearInterval(timer); timer = null; } };
      var play = function () {
        stop();
        if (ss.dataset.autoplay !== 'true' || reduce) return;
        timer = setInterval(function () { if (!document.hidden) go(i + 1); }, (parseInt(ss.dataset.speed, 10) || 5) * 1000);
      };
      track.addEventListener('scroll', debounce(function () {
        var w = track.clientWidth || 1; var n = Math.round(track.scrollLeft / w);
        if (n !== i) { i = n; mark(); }
      }, 80), { passive: true });
      if (prev) prev.addEventListener('click', function () { go(i - 1); play(); });
      if (next) next.addEventListener('click', function () { go(i + 1); play(); });
      dots.forEach(function (d) { d.addEventListener('click', function () { go(parseInt(d.dataset.ssDot, 10) || 0); play(); }); });
      ss.addEventListener('mouseenter', stop); ss.addEventListener('mouseleave', play);
      ss.addEventListener('focusin', stop); ss.addEventListener('focusout', play);
      track.addEventListener('touchstart', stop, { passive: true });
      track.addEventListener('touchend', play, { passive: true });
      window.addEventListener('resize', debounce(function () { go(i, true); }, 150));
      ss.addEventListener('ss:goto', function (e) { go(e.detail, false); stop(); });
      play();
    });
  }

  /* ---------- Cookie notice ---------- */
  function initCookie(root) {
    $$('[data-cookie]', root).forEach(function (el) {
      if (el.__init) return; el.__init = true;
      if (store.get('cookie-choice', null)) return;
      el.hidden = false;
      el.addEventListener('click', function (e) {
        var b = e.target.closest('[data-cookie-choice]'); if (!b) return;
        store.set('cookie-choice', b.dataset.cookieChoice); el.hidden = true;
      });
    });
  }

  /* ---------- Promo popup (once per N days / session) ---------- */
  function initPopup(root) {
    $$('[data-popup]', root).forEach(function (dlg) {
      if (dlg.__init) return; dlg.__init = true;
      var design = !!(window.Shopify && window.Shopify.designMode);
      if (design && dlg.dataset.preview !== 'true') return;
      var days = parseInt(dlg.dataset.days, 10) || 0;
      var delay = (parseFloat(dlg.dataset.delay) || 0) * 1000;
      var seen = function () {
        if (design) return false;
        if (days > 0) return Date.now() - (store.get('popup-seen', 0) || 0) < days * 86400000;
        try { return sessionStorage.getItem('stride:popup-seen') === '1'; } catch (e) { return false; }
      };
      var remember = function () {
        if (days > 0) store.set('popup-seen', Date.now());
        else { try { sessionStorage.setItem('stride:popup-seen', '1'); } catch (e) { /* ignore */ } }
      };
      if (seen()) return;
      setTimeout(function () {
        if (dlg.open || document.querySelector('dialog[open]') || (document.body.classList.contains('is-locked'))) return;
        if (typeof dlg.showModal === 'function') { dlg.showModal(); remember(); }
      }, design ? 300 : delay);
      dlg.addEventListener('click', function (e) { if (e.target === dlg || e.target.closest('[data-popup-close]')) dlg.close(); });
    });
  }

  /* ---------- Global init ---------- */
  function initAll(root) {
    initAnnouncement(root); initSliders(root); initSlideshow(root); initCountdown(root); initTabs(root);
    initCookie(root); initPopup(root);
    initGallery(root); initProductForms(root); initBundle(root);
    initRecentlyViewed(root); initRecommendations(root);
    Wishlist.sync();
  }

  document.addEventListener('DOMContentLoaded', function () {
    initHeader(); trackViewed(); initAll(document); Search.renderRecent();
    var logo = $('.header'); if (logo) logo.classList.add('is-ready');
  });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); initHeader(); });
  document.addEventListener('shopify:block:select', function (e) {
    var ss = e.target.closest && e.target.closest('[data-slideshow]'); if (!ss) return;
    var slides = $$('.slideshow__slide', ss), n = slides.indexOf(e.target.closest('.slideshow__slide'));
    if (n > -1) ss.dispatchEvent(new CustomEvent('ss:goto', { detail: n }));
  });

  /* ---------- Delegated events ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    var el;

    if ((el = t.closest('[data-open-drawer]'))) {
      e.preventDefault();
      var target = document.getElementById(el.dataset.openDrawer);
      if (el.dataset.openDrawer === 'CartDrawer' && S.settings.cartType === 'page') { window.location.href = S.routes.cart; return; }
      Drawer.open(target);
      if (el.dataset.openDrawer === 'SearchDrawer') Search.renderRecent();
      return;
    }
    if (t.closest('[data-close-drawer]') || t.matches('[data-overlay]')) { Drawer.close(); return; }

    if ((el = t.closest('[data-open-filters]'))) { Drawer.open($('[data-filters]')); return; }

    if ((el = t.closest('[data-mnav-toggle]'))) {
      var sub = document.getElementById(el.getAttribute('aria-controls'));
      var open = el.getAttribute('aria-expanded') === 'true';
      el.setAttribute('aria-expanded', String(!open));
      if (sub) sub.classList.toggle('is-open', !open);
      return;
    }

    if ((el = t.closest('[data-quick-add]'))) { e.preventDefault(); quickAdd(el); return; }
    if ((el = t.closest('[data-quick-view]'))) { e.preventDefault(); openQuickView(el.dataset.quickView); return; }
    if (t.closest('[data-quick-view-close]')) { var d = $('[data-qv-dialog]'); if (d) d.close(); return; }
    if (t.matches('[data-qv-dialog]')) { t.close(); return; } /* backdrop click on <dialog> */

    if ((el = t.closest('[data-buy-now]'))) { e.preventDefault(); buyNow(el); return; }

    if ((el = t.closest('[data-wishlist-toggle]'))) {
      e.preventDefault();
      var added = Wishlist.toggle(el.dataset.wishlistToggle);
      Toast.show(added ? S.strings.wishlistAdd.replace(/^Add to/, 'Added to') : S.strings.wishlistRemove.replace(/^Remove from/, 'Removed from'));
      if ($('[data-wishlist-list]') && !added) renderWishlistList();
      return;
    }

    if ((el = t.closest('[data-cart-qty]'))) {
      var line = parseInt(el.dataset.line, 10), current = parseInt(el.dataset.qty, 10), delta = parseInt(el.dataset.delta, 10);
      el.disabled = true;
      Cart.change(line, Math.max(0, current + delta)).catch(function (err) { Toast.show(err.message); el.disabled = false; });
      return;
    }
    if ((el = t.closest('[data-cart-remove]'))) {
      e.preventDefault();
      Cart.change(parseInt(el.dataset.line, 10), 0).catch(function (err) { Toast.show(err.message); });
      return;
    }

    if ((el = t.closest('[data-open-size-guide]'))) { var m = $('[data-size-guide]'); if (m) m.showModal(); return; }
    if ((el = t.closest('[data-close-modal]'))) { var dl = el.closest('dialog'); if (dl) dl.close(); return; }
    if (t.tagName === 'DIALOG' && t.classList.contains('modal')) { t.close(); return; }

    if (t.closest('[data-clear-recent]')) { store.set('recent-searches', []); Search.renderRecent(); return; }

    if ((el = t.closest('[data-filter-link]'))) { e.preventDefault(); updateCollection(el.getAttribute('href')); return; }
    if (t.closest('[data-psearch] a, [data-search-recent] a')) { var inp = $('[data-search-input]'); if (inp && inp.value) Search.save(inp.value); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') Drawer.close();
    Drawer.trap(e);
  });

  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f.matches('[data-product-form]')) { e.preventDefault(); submitProductForm(f).catch(function () { /* toast shown */ }); return; }
    if (f.matches('[data-search-form]')) { var i = $('[data-search-input]', f); if (i) Search.save(i.value); }
  });

  document.addEventListener('input', function (e) {
    if (e.target.matches('[data-search-input]')) {
      if (!S.settings.predictive) return;
      var q = e.target.value.trim();
      clearTimeout(Search.timer);
      Search.timer = setTimeout(function () { Search.query(q); }, 220);
    }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t.matches('[data-cart-qty-input]')) {
      var q = Math.max(0, parseInt(t.value, 10) || 0);
      Cart.change(parseInt(t.dataset.line, 10), q).catch(function (err) { Toast.show(err.message); });
      return;
    }
    if (t.closest('[data-filters-form]') || t.matches('[data-sort-select]')) { onFilterChange(); }
  });

  window.addEventListener('popstate', function () {
    if ($('[data-collection]')) updateCollection(window.location.pathname + window.location.search, false);
  });

  window.Stride.Cart = Cart;
  window.Stride.Wishlist = Wishlist;
})();
